import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendUp, TrendDown, Scales, PersonSimpleRun, Bicycle, PersonSimpleWalk, CheckCircle, MapPin, CalendarBlank } from "@phosphor-icons/react";

const HEAT_METRICS = [
  { id: "recovery", label: "Восстановление" },
  { id: "sleep_h", label: "Сон" },
  { id: "strain", label: "Нагрузка" },
];

const cellColor = (metric, v) => {
  if (v == null) return "rgba(255,255,255,0.05)";
  if (metric === "recovery") {
    if (v < 33) return "#ff3b30";
    if (v < 50) return "#ff9100";
    if (v < 67) return "#ffea00";
    if (v < 85) return "#7ed957";
    return "#00e676";
  }
  if (metric === "sleep_h") {
    if (v < 5.5) return "#ff3b30";
    if (v < 6.5) return "#ff9100";
    if (v < 7.5) return "#7ed957";
    return "#00e676";
  }
  if (v < 6) return "rgba(0,132,255,0.25)";
  if (v < 10) return "rgba(0,132,255,0.5)";
  if (v < 14) return "rgba(0,132,255,0.75)";
  return "#0084ff";
};

function HeatmapCard({ heatmap }) {
  const [metric, setMetric] = useState("recovery");
  const byDate = Object.fromEntries(heatmap.map((d) => [d.date, d]));
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 364);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const weeks = [];
  const cur = new Date(start);
  while (cur <= today) {
    const col = [];
    for (let i = 0; i < 7 && cur <= today; i++) {
      col.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(col);
  }
  const monthLabels = weeks.map((w, i) => {
    const d = new Date(w[0]);
    return d.getDate() <= 7 ? d.toLocaleDateString("ru-RU", { month: "short" }) : (i === 0 ? d.toLocaleDateString("ru-RU", { month: "short" }) : "");
  });
  const unit = metric === "recovery" ? "%" : metric === "sleep_h" ? " ч" : "";
  return (
    <div className="glass-card p-5" data-testid="heatmap-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CalendarBlank size={18} weight="duotone" className="text-primary" />
        <h3 className="text-sm font-semibold flex-1">Год одним взглядом</h3>
        {HEAT_METRICS.map((m) => (
          <button key={m.id} onClick={() => setMetric(m.id)} data-testid={`heatmap-metric-${m.id}`}
            className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${metric === m.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto pb-1" data-testid="heatmap-grid">
        <div className="flex gap-[3px] text-[8px] text-muted-foreground mb-1 min-w-max">
          {monthLabels.map((l, i) => <div key={i} className="w-[10px] shrink-0">{l && <span className="absolute">{l}</span>}</div>)}
        </div>
        <div className="flex gap-[3px] min-w-max">
          {weeks.map((w, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {w.map((ds) => {
                const v = byDate[ds]?.[metric];
                return <div key={ds} className="w-[10px] h-[10px] rounded-[2px]"
                  style={{ background: cellColor(metric, v) }}
                  title={`${new Date(ds + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}: ${v != null ? v + unit : "нет данных"}`} />;
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2 text-[10px] text-muted-foreground">
        Хуже {["#ff3b30", "#ff9100", "#ffea00", "#7ed957", "#00e676"].map((c) => (
          <span key={c} className="w-[10px] h-[10px] rounded-[2px] inline-block" style={{ background: metric === "strain" ? undefined : c, ...(metric === "strain" ? { background: "rgba(0,132,255,0.6)" } : {}) }} />
        ))} лучше
      </div>
    </div>
  );
}

function TrendsCard({ trends }) {
  return (
    <div className="glass-card p-5" data-testid="trends-card">
      <h3 className="text-sm font-semibold mb-3">4 недели vs предыдущие 4</h3>
      <div className="space-y-2.5">
        {trends.map((t) => {
          const good = t.delta == null || t.good_dir === 0 ? null : (t.delta * t.good_dir >= 0);
          return (
            <div key={t.key} className="flex items-center gap-3" data-testid={`trend-${t.key}`}>
              <span className="text-sm flex-1">{t.label}</span>
              <span className="text-sm font-bold num">{t.current ?? "—"}{t.current != null && t.unit ? ` ${t.unit}` : ""}</span>
              {t.delta != null && (
                <span className={`flex items-center gap-0.5 text-xs num font-semibold min-w-[70px] justify-end ${good == null ? "text-muted-foreground" : good ? "text-[#00e676]" : "text-[#ff3b30]"}`}>
                  {t.delta > 0 ? <TrendUp size={13} /> : t.delta < 0 ? <TrendDown size={13} /> : null}
                  {t.delta > 0 ? "+" : ""}{t.delta}{t.unit}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const zoneStyle = { danger: "text-[#ff3b30]", warning: "text-[#ff9100]", optimal: "text-[#00e676]", detrain: "text-[#4dabf5]", nodata: "text-muted-foreground" };

function LoadCard({ load }) {
  const pct = load.ratio != null ? Math.min(100, (load.ratio / 2) * 100) : 0;
  return (
    <div className="glass-card p-5" data-testid="load-card">
      <div className="flex items-center gap-2 mb-3">
        <Scales size={18} weight="duotone" className="text-[#ff9100]" />
        <h3 className="text-sm font-semibold flex-1">Баланс нагрузки (7 дн. / 28 дн.)</h3>
        {load.ratio != null && <span className={`font-heading text-xl font-extrabold num ${zoneStyle[load.zone]}`}>{load.ratio}×</span>}
      </div>
      {load.ratio != null && (
        <div className="relative h-2 rounded-full mb-3 overflow-hidden"
          style={{ background: "linear-gradient(90deg,#4dabf5 0%,#4dabf5 40%,#00e676 40%,#00e676 65%,#ff9100 65%,#ff9100 75%,#ff3b30 75%)" }}>
          <div className="absolute top-[-2px] w-[3px] h-3 bg-white rounded" style={{ left: `${pct}%` }} />
        </div>
      )}
      <p className={`text-xs ${zoneStyle[load.zone]}`} data-testid="load-message">{load.message}</p>
      <div className="flex gap-4 mt-3 text-[11px] text-muted-foreground num">
        <span>Неделя: {load.acute ?? "—"}</span>
        <span>Месяц: {load.chronic ?? "—"}</span>
        <span>Восст. 7 дн: {load.rec7 ?? "—"}%</span>
      </div>
    </div>
  );
}

const sportIcons = { run: PersonSimpleRun, bike: Bicycle, walk: PersonSimpleWalk };

function GpsCard({ gps }) {
  if (!gps) return null;
  return (
    <div className="glass-card p-5" data-testid="gps-impact-card">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={18} weight="duotone" className="text-[#ff9100]" />
        <h3 className="text-sm font-semibold">GPS-тренировки и восстановление</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Наутро после GPS-тренировки восстановление в среднем <b className="text-foreground num">{gps.after}%</b> против <b className="text-foreground num">{gps.baseline}%</b> в остальные дни
        {gps.diff != null && <span className={gps.diff >= 0 ? "text-[#00e676]" : "text-[#ff9100]"}> ({gps.diff > 0 ? "+" : ""}{gps.diff}%)</span>}.
        {gps.diff != null && gps.diff < -5 && " Планируйте лёгкий день после тренировок."}
      </p>
      <div className="space-y-1.5">
        {gps.sports.map((s) => {
          const Icon = sportIcons[s.sport];
          return (
            <div key={s.sport} className="flex items-center gap-2 text-xs" data-testid={`gps-impact-${s.sport}`}>
              <Icon size={14} className="text-muted-foreground" />
              <span className="flex-1">{s.label} · {s.days} дн.</span>
              <span className="num font-semibold">восст. наутро {s.after}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HabitsCard({ habits }) {
  if (!habits.length) return null;
  return (
    <div className="glass-card p-5" data-testid="habits-impact-card">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle size={18} weight="duotone" className="text-[#00e676]" />
        <h3 className="text-sm font-semibold">Что влияет именно на вас</h3>
      </div>
      <div className="space-y-2.5">
        {habits.map((h) => (
          <div key={h.name} className="text-xs" data-testid={`habit-impact-${h.name}`}>
            <div className="flex items-center gap-2">
              <span className="font-semibold flex-1">{h.name}</span>
              <span className={`num font-bold ${h.rec_diff >= 0 ? "text-[#00e676]" : "text-[#ff3b30]"}`}>{h.rec_diff > 0 ? "+" : ""}{h.rec_diff}% восст.</span>
            </div>
            <div className="text-muted-foreground mt-0.5">
              наутро после выполнения: {h.rec_done}% против {h.rec_not}%
              {h.sleep_diff != null && Math.abs(h.sleep_diff) >= 0.1 && <> · сон {h.sleep_diff > 0 ? "+" : ""}{h.sleep_diff} ч</>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrendsTab({ member = "" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/health/deep${member ? `?member=${member}` : ""}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [member]);

  if (err) return <p className="text-sm text-muted-foreground" data-testid="trends-error">Не удалось загрузить тренды — нужны данные Whoop.</p>;
  if (!data) return <div className="glass-card h-48 animate-pulse" data-testid="trends-loading" />;

  return (
    <div className="space-y-4" data-testid="trends-tab">
      <HeatmapCard heatmap={data.heatmap} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TrendsCard trends={data.trends} />
        <LoadCard load={data.load} />
      </div>
      <GpsCard gps={data.gps} />
      <HabitsCard habits={data.habits} />
      {!data.gps && <p className="text-[11px] text-muted-foreground">Запишите 3+ GPS-тренировки — появится анализ их влияния на восстановление.</p>}
      {!data.habits.length && <p className="text-[11px] text-muted-foreground">Отмечайте привычки 5+ дней — покажем, какие из них сильнее всего влияют на ваш сон и восстановление.</p>}
    </div>
  );
}
